import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer, Area,
  ComposedChart, ReferenceLine,
} from 'recharts';
import { useConfig } from '../store/config';
import { useFilters } from '../store/filters';
import { fmtInt, formatDate } from '../lib/format';
import type { Snapshot } from '../lib/snapshots';

type Mode = 'counts' | 'closure' | 'plan';

// Wandelt die History-Snapshots in Datenpunkte für den Chart.
// team='' → globale Counts. team='Door' → Counts aus snap.metrics.byTeam.
function buildChartData(history: Snapshot[], team: string) {
  return history.map(s => {
    const m = s.metrics ?? {};

    // Wenn ein Team gewählt ist, ziehen wir Counts aus byTeam — sofern verfügbar.
    // Bugs/Stories getrennt sind in V1's byTeam nicht enthalten, deshalb hier nur
    // die Gesamt-Counts pro Team. Open/Closed gibt's pro Team.
    if (team) {
      const tm = (m.byTeam ?? {})[team] ?? {};
      const stories = tm.stories ?? 0;
      const bugs    = tm.bugs    ?? 0;
      const closed  = tm.closed  ?? 0;
      const open    = tm.open    ?? 0;
      const total   = stories + bugs;
      return {
        dateISO: s.date,
        label: formatDate(s.date),
        // Pro Team haben wir nur summary counts (kein Split nach Type)
        openStories:   null,
        openBugs:      null,
        closedStories: null,
        closedBugs:    null,
        openTeam:      open,
        closedTeam:    closed,
        storiesTeam:   stories,
        bugsTeam:      bugs,
        closurePctTotal: total > 0 ? Math.round((closed / total) * 100) : null,
        // Plan-PT pro Team gibt's noch nicht — kommt mit V2-Snapshots ab Phase 2.9.3+1
        roughTotal:       null,
        refinedStoriesPT: null,
        refinedBugsPT:    null,
        refinedTotalPT:   null,
        capacityTotal:    null,
        // Hilfsfelder für die globale Sicht (= 0 bei Team-Auswahl)
        stories: 0, bugs: 0, epics: 0,
        closurePctStories: null,
        closurePctBugs:    null,
      };
    }

    // Globale Sicht
    const stories = m.stories ?? 0;
    const bugs    = m.bugs    ?? 0;
    const closedS = m.closedStories ?? 0;
    const closedB = m.closedBugs    ?? 0;
    const openS   = m.openStories   ?? Math.max(0, stories - closedS);
    const openB   = m.openBugs      ?? Math.max(0, bugs    - closedB);
    return {
      dateISO: s.date,
      label: formatDate(s.date),
      stories,
      bugs,
      epics: m.epics ?? 0,
      closedStories: closedS,
      closedBugs:    closedB,
      openStories:   openS,
      openBugs:      openB,
      // Team-Felder bleiben leer in der globalen Sicht
      openTeam:      null,
      closedTeam:    null,
      storiesTeam:   null,
      bugsTeam:      null,
      closurePctTotal: null,
      // Closure-Rate %
      closurePctStories: stories > 0 ? Math.round((closedS / stories) * 100) : null,
      closurePctBugs:    bugs    > 0 ? Math.round((closedB / bugs)    * 100) : null,
      // Plan-PT (V2-only)
      roughTotal:       m.roughTotal       ?? null,
      refinedStoriesPT: m.refinedStoriesPT ?? null,
      refinedBugsPT:    m.refinedBugsPT    ?? null,
      refinedTotalPT:   (m.refinedStoriesPT == null && m.refinedBugsPT == null)
        ? null
        : (m.refinedStoriesPT ?? 0) + (m.refinedBugsPT ?? 0),
      capacityTotal:    m.capacityTotal    ?? null,
    };
  });
}

// Sammelt alle Teams, die in irgendeinem Snapshot ein byTeam-Entry haben.
function teamsInHistory(history: Snapshot[]): string[] {
  const set = new Set<string>();
  for (const s of history) {
    const bt = s.metrics?.byTeam;
    if (!bt) continue;
    for (const t of Object.keys(bt)) if (t && t !== '—') set.add(t);
  }
  return Array.from(set).sort();
}

// Chart über die Import-Snapshots — drei Modi:
//   • Counts        — open/closed Stories und Bugs als Linien
//   • Closure-Rate  — % closed pro Typ
//   • Plan-Burn-Up  — Σ refined PT vs. Rough vs. Kapa (nur ab V2-Snapshots)
export function HistoryChart() {
  const history = useConfig(s => s.history);
  const theme = useTheme();
  const [mode, setMode] = useState<Mode>('counts');
  // Team-Selector ist bidirektional an den globalen Filter gebunden.
  const team       = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);

  const teams = useMemo(() => teamsInHistory(history), [history]);
  const data  = useMemo(() => buildChartData(history, team), [history, team]);

  if (!data.length) return null;

  // V2-Snapshots mit Plan-PT — nur in der globalen Sicht relevant (Team-Sicht
  // hat noch keine Plan-PT, das markiert sich im Modus-Toggle).
  const v2Count = team
    ? 0
    : data.filter(d => d.refinedTotalPT != null || d.roughTotal != null).length;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <HistoryIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="overline" color="text.secondary">Refinement-Burn-Up</Typography>
            <Typography variant="body2" color="text.secondary">
              {data.length} Snapshots seit {data[0].label}
              {team && <> · für Team <b>{team}</b></>}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }} disabled={teams.length === 0}>
            <InputLabel>Team</InputLabel>
            <Select
              value={team}
              label="Team"
              onChange={(e) => setFilters({ team: e.target.value })}
              startAdornment={<GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
            >
              <MenuItem value=""><em>Alle Teams</em></MenuItem>
              {teams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small" value={mode} exclusive onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="counts">Counts</ToggleButton>
            <ToggleButton value="closure">Closure-Rate</ToggleButton>
            <ToggleButton value="plan" disabled={v2Count === 0 || team !== ''}>
              Plan-Burn-Up{v2Count > 0 && ` (${v2Count})`}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {team && mode === 'plan' ? (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Plan-Burn-Up pro Team braucht erweiterte Snapshot-Metriken, die wir aktuell nur global erfassen.
            Schalte oben rechts auf <b>Alle Teams</b>, um die Plan-Sicht zu sehen.
          </Alert>
        ) : mode === 'plan' && v2Count === 0 ? (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Plan-Burn-Up braucht V2-Snapshots mit Refined-PT-Werten. Importiere einen neuen CSV-Snapshot —
            beim nächsten Import schreiben wir Σ Rough, Σ refined Stories/Bugs und Kapazität in die History.
          </Alert>
        ) : (
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              {mode === 'counts' ? (
                <LineChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} width={56} />
                  <ReTooltip content={<HistTooltip suffix="" />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                  {team ? (
                    <>
                      <Line name="Open"   type="monotone" dataKey="openTeam"   stroke="#c62828" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                      <Line name="Closed" type="monotone" dataKey="closedTeam" stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    </>
                  ) : (
                    <>
                      <Line name="Open Stories"   type="monotone" dataKey="openStories"   stroke="#2e7d32" strokeWidth={2} dot={{ r: 3 }} />
                      <Line name="Closed Stories" type="monotone" dataKey="closedStories" stroke="#81c784" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
                      <Line name="Open Bugs"      type="monotone" dataKey="openBugs"      stroke="#c62828" strokeWidth={2} dot={{ r: 3 }} />
                      <Line name="Closed Bugs"    type="monotone" dataKey="closedBugs"    stroke="#ef9a9a" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
                    </>
                  )}
                </LineChart>
              ) : mode === 'closure' ? (
                <LineChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} width={48} />
                  <ReTooltip content={<HistTooltip suffix="%" />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                  {team ? (
                    <Line name="Closed %" type="monotone" dataKey="closurePctTotal" stroke="#1976d2" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                  ) : (
                    <>
                      <Line name="Stories closed %" type="monotone" dataKey="closurePctStories" stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                      <Line name="Bugs closed %"    type="monotone" dataKey="closurePctBugs"    stroke="#c62828" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    </>
                  )}
                </LineChart>
              ) : (
                <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} width={56} />
                  <ReTooltip content={<HistTooltip suffix=" PT" />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                  <ReferenceLine y={0} stroke={theme.palette.divider} />
                  <Area
                    name="Σ Refined (Stories+Bugs)"
                    type="monotone"
                    dataKey="refinedTotalPT"
                    fill="#1976d2"
                    fillOpacity={0.18}
                    stroke="#1976d2"
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    connectNulls
                  />
                  <Line name="Σ Rough (Epics)" type="monotone" dataKey="roughTotal"    stroke="#8250df" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} connectNulls />
                  <Line name="Σ Kapazität"      type="monotone" dataKey="capacityTotal" stroke="#5e7186" strokeWidth={2} strokeDasharray="2 3" dot={{ r: 3 }} connectNulls />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          {data.length > 0 && (
            <Chip size="small" label={`erster Snap: ${data[0].label}`} variant="outlined" />
          )}
          {data.length > 1 && (
            <Chip size="small" label={`letzter Snap: ${data[data.length - 1].label}`} variant="outlined" />
          )}
          {v2Count > 0 && (
            <Chip size="small" color="primary" label={`${v2Count} V2-Snapshot(s) mit Plan-PT`} />
          )}
        </Stack>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {mode === 'counts' && (team
            ? `Open- und Closed-Items von Team „${team}" über die Snapshots. Stories und Bugs sind hier kombiniert (V1-Snapshots speichern pro Team nur Summen).`
            : 'Open- und Closed-Linien pro Typ. Strichlierung = closed. Bei steigender Open- und Closed-Linie wächst der Backlog gleichzeitig, weil neue Tickets schneller kommen, als sie geschlossen werden.')}
          {mode === 'closure' && (team
            ? `Closure-Rate für Team „${team}" = % aller Items, die geschlossen sind.`
            : 'Closure-Rate = % aller Stories/Bugs, die geschlossen sind. Über Wochen steigend = wir holen auf; flach oder fallend = neue Tickets übertreffen die Schließrate.')}
          {mode === 'plan' && 'Σ Refined Kids (blaue Fläche) sollte sich der Σ Rough (lila gestrichelt) annähern. Σ Kapazität (grau) als Decken-Referenz. Wächst die blaue Fläche an die lila Linie heran, ist das Refinement durch.'}
        </Typography>
      </CardContent>
    </Card>
  );
}

type RechartsTooltipProps = {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
};

function HistTooltip({ active, payload, label, suffix }: RechartsTooltipProps & { suffix: string }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 3,
        p: 1.5,
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{label}</Typography>
      <Stack spacing={0.5}>
        {payload.map((p, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: p.color }} />
            <Typography variant="caption" sx={{ flex: 1 }}>{p.name}</Typography>
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
              {p.value == null ? '–' : `${fmtInt(p.value)}${suffix}`}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
